/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  FloatingFormItem,
  InputDate,
  PhoneFormField,
  ZipFormField,
} from '@/components/_common';
import CpfCnpjInput from '@/components/_common/CpfCnpjInput';
import { FloatingFormItemProps } from '@/components/_common/FloatingFormItem';
import { PortoPolicyVoucher } from '@/core/domain/porto/policies/porto-policies.types';
import { useDefaultEmptyFieldError } from '@/hooks';
import { applyCpfCnpjMask, applyMask, removeMask } from '@/utils/mask';
import { cellphoneMask, cepMask } from '@/utils/mask/commonMasks';
import { Card, Col, Empty, Form, Input, Row, Skeleton } from 'antd';
import { NamePath } from 'antd/lib/form/interface';
import moment from 'moment';
import { FunctionComponent, ReactElement } from 'react';

interface Props {
  customer?: PortoPolicyVoucher['customer'];
  address?: PortoPolicyVoucher['customer']['address'] & { complement?: string };
  isLoading: boolean;
}

interface CustomerFieldProps<T> {
  label: string;
  name?: NamePath;
  value?: T;
  required?: boolean;
  children?: ReactElement;
  formatValue?: (value: T) => any;
  component?: FunctionComponent | ((p: any) => any);
  componentProps?: any;
  initialValue?: any;
  rules?: FloatingFormItemProps['rules'];
}

function CustomerField<T = any>({
  formatValue = (value: T) => value,
  component,
  ...props
}: CustomerFieldProps<T>) {
  const fieldRequired = useDefaultEmptyFieldError();

  const Component = component || FloatingFormItem;

  if (!props.value && props.name && (props.children || component)) {
    return (
      <Component
        label={props.label}
        name={props.name}
        rules={
          props.required && !component
            ? [fieldRequired, ...(props.rules || [])]
            : !component
            ? props.rules
            : undefined
        }
        initialValue={props.initialValue}
        required={props.required}
        {...props?.componentProps}
      >
        {props.children}
      </Component>
    );
  }

  return (
    <div>
      <span style={{ display: 'block' }}>
        {props.label}{' '}
        {props.required ? <span style={{ color: 'red' }}>*</span> : null}
      </span>
      <strong style={{ display: 'block' }}>
        {props.value ? formatValue(props.value) : 'Sem informação'}
      </strong>
    </div>
  );
}

export default function PortoCPFCustomer({
  isLoading,
  customer,
  address,
}: Props) {
  const zipCode = address?.cep ? removeMask(address.cep) : undefined;

  if (!customer)
    return (
      <Skeleton loading={isLoading}>
        <Empty description="Não há dados de cliente para este CPF" />
      </Skeleton>
    );

  return (
    <>
      <Skeleton loading={isLoading}>
        <Form.Item shouldUpdate noStyle>
          {({ getFieldError }) => {
            const error = getFieldError('voucher');

            return (
              <>
                <Card
                  style={{
                    borderRadius: 10,
                    borderColor: error.length ? 'red' : undefined,
                  }}
                >
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <CustomerField
                        label="Nome"
                        name={['voucher', 'customer', 'name']}
                        required
                        value={customer.name}
                      >
                        <Input />
                      </CustomerField>
                    </Col>

                    <Col span={12}>
                      <CustomerField
                        label="CPF"
                        name={['voucher', 'customer', 'documentNumber']}
                        required
                        value={customer.documentNumber}
                        formatValue={(value) => applyCpfCnpjMask(value)}
                      >
                        <CpfCnpjInput />
                      </CustomerField>
                    </Col>

                    <Col span={12}>
                      <CustomerField
                        label="Dt. nascimento"
                        name={['voucher', 'customer', 'birthDate']}
                        required
                        value={
                          customer?.birthDate
                            ? moment(customer?.birthDate)
                            : undefined
                        }
                        formatValue={(value) => value.format('DD/MM/YYYY')}
                      >
                        <InputDate />
                      </CustomerField>
                    </Col>

                    <Col span={12}>
                      <CustomerField
                        label="Telefone"
                        name={['voucher', 'customer', 'phone']}
                        required
                        value={
                          customer?.phone?.length >= 10
                            ? customer.phone
                            : undefined
                        }
                        formatValue={(value) => applyMask(value, cellphoneMask)}
                        component={PhoneFormField}
                        initialValue={customer.phone}
                        componentProps={{ asPhone: false }}
                      />
                    </Col>

                    <Col span={12}>
                      <CustomerField
                        label="E-mail"
                        name={['voucher', 'customer', 'email']}
                        required
                        value={customer?.email}
                        rules={[{ type: 'email', message: 'E-mail inválido' }]}
                      >
                        <Input />
                      </CustomerField>
                    </Col>

                    <Col span={24}>
                      <strong style={{ color: 'darkgray' }}>Endereço</strong>
                    </Col>

                    <Col span={24}>
                      <CustomerField
                        label="CEP"
                        value={
                          (zipCode?.length || 0) === 8 ? zipCode : undefined
                        }
                        name={['voucher', 'address', 'cep']}
                        component={ZipFormField}
                        componentProps={{
                          fieldName: ['voucher', 'address', 'cep'],
                          initialValue: zipCode,
                        }}
                        formatValue={(value) => applyMask(value, cepMask)}
                      />
                    </Col>

                    <Col span={24}>
                      <CustomerField
                        label="Logradouro"
                        required
                        value={address?.street}
                        name={['voucher', 'address', 'street']}
                      >
                        <Input />
                      </CustomerField>
                    </Col>

                    <Col span={6}>
                      <CustomerField
                        label="Nº"
                        required
                        value={address?.number}
                        name={['voucher', 'address', 'number']}
                      >
                        <Input />
                      </CustomerField>
                    </Col>

                    <Col span={18}>
                      <CustomerField
                        label="Complemento"
                        value={address?.complement}
                        name={['voucher', 'address', 'complement']}
                      >
                        <Input />
                      </CustomerField>
                    </Col>

                    <Col span={24}>
                      <CustomerField
                        label="Bairro"
                        required
                        value={address?.district}
                        name={['voucher', 'address', 'district']}
                      >
                        <Input />
                      </CustomerField>
                    </Col>

                    <Col span={18}>
                      <CustomerField
                        label="Cidade"
                        required
                        value={address?.city}
                        name={['voucher', 'address', 'city']}
                      >
                        <Input />
                      </CustomerField>
                    </Col>

                    <Col span={6}>
                      <CustomerField
                        label="Estado"
                        required
                        value={address?.state}
                        name={['voucher', 'address', 'state']}
                      >
                        <Input />
                      </CustomerField>
                    </Col>
                  </Row>
                </Card>

                {!!error.length && (
                  <span style={{ color: 'red' }}>{error[0]}</span>
                )}
              </>
            );
          }}
        </Form.Item>
      </Skeleton>
    </>
  );
}
